import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import {
    enumerateDateInputsInRange,
    formatDateForDisplay,
    formatDateForInput,
    formatPromotionDate,
    getPromotionPeriodRange,
    getTodayForDateInput,
    parseDisplayDateToInput,
    isDateWithinPromotionStartBounds,
} from './promoUtils.js';
import './PromotionStartDatePicker.css';

const WEEKDAYS = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'];
const MONTHS = [
    'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
    'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
];

const parseDateInput = (dateInput) => new Date(`${dateInput}T12:00:00`);

const toDateInput = (date) => formatDateForInput(date.toISOString());

const isDisabledDay = (dateInput, minDate, maxDate) => {
    if (minDate && dateInput < minDate) return true;
    if (maxDate && dateInput > maxDate) return true;
    return false;
};

function PromotionStartDatePicker({
    value,
    onChange,
    minDate,
    maxDate,
    durationDays,
    eventEndDate,
    inputClassName = 'input-field full-width',
    placeholder = 'dd.mm.rrrr',
}) {
    const containerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState(() => formatDateForDisplay(value));
    const [hoverDate, setHoverDate] = useState(null);
    const [visibleMonth, setVisibleMonth] = useState(() => {
        const base = value || minDate || getTodayForDateInput();
        const date = parseDateInput(base);
        return { year: date.getFullYear(), month: date.getMonth() };
    });

    useEffect(() => {
        setDisplayValue(formatDateForDisplay(value));
    }, [value]);

    useEffect(() => {
        if (!value) return;
        const date = parseDateInput(value);
        setVisibleMonth({ year: date.getFullYear(), month: date.getMonth() });
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setHoverDate(null);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const activeRange = useMemo(() => {
        const anchor = hoverDate || value;
        if (!anchor || !durationDays) return null;
        return getPromotionPeriodRange(anchor, durationDays, eventEndDate);
    }, [hoverDate, value, durationDays, eventEndDate]);

    const highlightedDates = useMemo(() => {
        if (!activeRange) return new Set();
        return new Set(enumerateDateInputsInRange(activeRange.start, activeRange.end));
    }, [activeRange]);

    const calendarDays = useMemo(() => {
        const firstDay = new Date(visibleMonth.year, visibleMonth.month, 1);
        const startOffset = (firstDay.getDay() + 6) % 7;
        const gridStart = new Date(visibleMonth.year, visibleMonth.month, 1 - startOffset);

        return Array.from({ length: 42 }, (_, index) => {
            const date = new Date(gridStart);
            date.setDate(gridStart.getDate() + index);
            const dateInput = toDateInput(date);

            return {
                dateInput,
                day: date.getDate(),
                isCurrentMonth: date.getMonth() === visibleMonth.month,
                isDisabled: isDisabledDay(dateInput, minDate, maxDate),
                isSelected: value === dateInput,
                isInRange: highlightedDates.has(dateInput),
                isRangeStart: activeRange?.start === dateInput,
                isRangeEnd: activeRange?.end === dateInput,
            };
        });
    }, [visibleMonth, value, minDate, maxDate, highlightedDates, activeRange]);

    const commitDisplayValue = (nextDisplay) => {
        if (!nextDisplay.trim()) {
            setDisplayValue('');
            onChange('');
            return;
        }

        const parsed = parseDisplayDateToInput(nextDisplay);
        if (parsed) {
            if (!isDateWithinPromotionStartBounds(parsed, minDate, maxDate)) {
                setDisplayValue(formatDateForDisplay(value));
                return;
            }
            setDisplayValue(formatDateForDisplay(parsed));
            onChange(parsed);
        } else {
            setDisplayValue(formatDateForDisplay(value));
        }
    };

    const handleDaySelect = (dateInput, isDisabled) => {
        if (isDisabled) return;
        onChange(dateInput);
        setDisplayValue(formatDateForDisplay(dateInput));
        setHoverDate(null);
        setIsOpen(false);
    };

    const shiftMonth = (delta) => {
        setVisibleMonth((current) => {
            const next = new Date(current.year, current.month + delta, 1);
            return { year: next.getFullYear(), month: next.getMonth() };
        });
    };

    return (
        <div className="promotion-date-picker" ref={containerRef}>
            <div className="promotion-date-picker-input-wrap">
                <input
                    type="text"
                    className={`promotion-date-picker-input ${inputClassName}`}
                    value={displayValue}
                    placeholder={placeholder}
                    inputMode="numeric"
                    onChange={(event) => setDisplayValue(event.target.value)}
                    onBlur={() => commitDisplayValue(displayValue)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            commitDisplayValue(displayValue);
                        }
                    }}
                />
                <button
                    type="button"
                    className={`promotion-date-picker-trigger ${isOpen ? 'is-open' : ''}`}
                    aria-label="Otwórz kalendarz"
                    onClick={() => setIsOpen((open) => !open)}
                >
                    <Calendar size={20} />
                </button>
            </div>

            {isOpen && (
                <div className="promotion-date-picker-popover">
                    <div className="promotion-date-picker-header">
                        <div className="promotion-date-picker-title">
                            {MONTHS[visibleMonth.month]} {visibleMonth.year}
                        </div>
                        <div className="promotion-date-picker-nav">
                            <button type="button" aria-label="Poprzedni miesiąc" onClick={() => shiftMonth(-1)}>
                                <ChevronLeft size={18} />
                            </button>
                            <button type="button" aria-label="Następny miesiąc" onClick={() => shiftMonth(1)}>
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="promotion-date-picker-weekdays">
                        {WEEKDAYS.map((weekday) => (
                            <div key={weekday} className="promotion-date-picker-weekday">{weekday}</div>
                        ))}
                    </div>

                    <div className="promotion-date-picker-grid">
                        {calendarDays.map((day) => {
                            const classNames = ['promotion-date-picker-day'];
                            if (!day.isCurrentMonth) classNames.push('is-outside');
                            if (day.isDisabled) classNames.push('is-disabled');
                            if (day.isInRange) classNames.push('is-in-range');
                            if (day.isRangeStart) classNames.push('is-range-start');
                            if (day.isRangeEnd) classNames.push('is-range-end');
                            if (day.isSelected) classNames.push('is-selected');

                            return (
                                <button
                                    key={day.dateInput}
                                    type="button"
                                    className={classNames.join(' ')}
                                    disabled={day.isDisabled}
                                    onMouseEnter={() => !day.isDisabled && setHoverDate(day.dateInput)}
                                    onMouseLeave={() => setHoverDate(null)}
                                    onClick={() => handleDaySelect(day.dateInput, day.isDisabled)}
                                >
                                    {day.day}
                                </button>
                            );
                        })}
                    </div>

                    {durationDays > 0 && (
                        <div className="promotion-date-picker-legend">
                            Pakiet obejmuje <strong>{durationDays} dni</strong> promowania.
                            {activeRange && (
                                <div className="promotion-date-picker-range-preview">
                                    Zakres: {formatPromotionDate(activeRange.start)} – {formatPromotionDate(activeRange.end)}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PromotionStartDatePicker;
